import isEmpty from 'lodash/isEmpty';
import { useAppendDraftComment } from 'pages/WellGuardian/WIR/hooks';
import { getKomTechnicalWirReactiveScopeData } from 'pages/WellGuardian/WIR/utils';
import {
  WIRCreateJourneyStepsKeys,
  WIRKofMeetingTechnicalWirSections,
  WIRStageGate2Sections,
} from 'pages/WellGuardian/WIR/wir.constants';
import { useCallback, useMemo } from 'react';
import { useCountryState, useSelectedWIR } from 'redux/WellGuardian/selectors';
import wellGuardianApiClient from 'services/networking/wellGuardianApiClient';
import { WIR, WIRPhaseTypes } from 'types/wellGuardian';

import { getKickOffPhaseName } from '../utils/getSectionData';

export const useSectionWellInformationData = () => {
  const wirFromState = useSelectedWIR();
  const { appendDraftComment } = useAppendDraftComment();
  const countryState = useCountryState();

  const wellInformationSectionPayload = useMemo(() => {
    const { phase, wellInformation } = getKomTechnicalWirReactiveScopeData(
      wirFromState,
      countryState,
    );
    const hudDate = wellInformation?.hudDate ?? new Date().toISOString();
    const lastHud = wellInformation?.lastHud;
    const hudTag = wellInformation?.hudTag;
    const prodDataDate = wellInformation?.prodDataDate ?? new Date().toISOString();
    const choke = wellInformation?.choke;
    const gasRate = wellInformation?.gasRate;
    const waterRate = wellInformation?.waterRate;
    const oilRate = wellInformation?.oilRate;
    const GOR = wellInformation?.GOR;
    const exportRate = wellInformation?.exportRate;
    const co2 = wellInformation?.co2;
    const whsip = wellInformation?.whsip;
    const whfp = wellInformation?.whfp;
    const whft = wellInformation?.whft;
    const mewhp = wellInformation?.mewhp;
    const bht = wellInformation?.bht;
    const bhp = wellInformation?.bhp;
    const fracPressure = wellInformation?.fracPressure;
    const h2s = wellInformation?.h2s;
    const attachments = wellInformation?.attachments;
    const comments = appendDraftComment(wellInformation?.comments || []);
    const sectionCompleteConfirmation = wellInformation?.sectionCompleteConfirmation;

    const isTicked =
      !isEmpty(lastHud) &&
      !isEmpty(choke) &&
      !isEmpty(gasRate) &&
      !isEmpty(waterRate) &&
      !isEmpty(oilRate) &&
      !isEmpty(GOR) &&
      !isEmpty(exportRate) &&
      !isEmpty(co2) &&
      !isEmpty(whsip) &&
      !isEmpty(whfp) &&
      !isEmpty(whft) &&
      !isEmpty(mewhp) &&
      !isEmpty(bht) &&
      !isEmpty(bhp) &&
      !isEmpty(fracPressure) &&
      !isEmpty(h2s) &&
      !isEmpty(hudTag);

    return {
      hudDate,
      isTicked,
      attachments,
      hudTag,
      prodDataDate,
      lastHud,
      choke,
      gasRate,
      waterRate,
      oilRate,
      GOR,
      exportRate,
      co2,
      whsip,
      whfp,
      whft,
      mewhp,
      bht,
      bhp,
      fracPressure,
      h2s,
      sectionCompleteConfirmation,
      comments,
      lastUpdatedDate: new Date().toISOString(),
      assignedTo: wellInformation?.assignedTo || '',
      phase,
    };
  }, [wirFromState, appendDraftComment, countryState]);

  const saveWellInformationSection = useCallback(
    async (section: string, processSaveResponse: (wirResponse: WIR | null) => void) => {
      const phaseName = getKickOffPhaseName(countryState);
      if (
        wirFromState?.wirNumber &&
        (section === WIRKofMeetingTechnicalWirSections.wellInformation ||
          section === WIRStageGate2Sections.wellInformation)
      ) {
        processSaveResponse(
          await wellGuardianApiClient.updateWIRWellInformationSection(
            wirFromState?.wirNumber,
            wellInformationSectionPayload,
            wellInformationSectionPayload.phase === WIRCreateJourneyStepsKeys.REACTIVE_SCOPE
              ? WIRPhaseTypes.REACTIVE_SCOPE
              : phaseName,
            countryState,
          ),
        );
      }
    },
    [wirFromState, wellInformationSectionPayload, countryState],
  );

  return {
    saveWellInformationSection,
    wellInformationSectionPayload,
  };
};
